import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import { BsCaretDownFill, BsCaretRightFill } from 'react-icons/bs';

import { useCodeDefinitionsAsOptions } from '../../../../v2/js/hooks/useCodeDefinitions';
import {
  BookingRequestRead,
  BookingRequestsUpdateService,
  ConnectedFacility,
  ConnectedFacilityService,
  ExpectedExpensesEnum,
  ProviderBookingRequestWriteDetail,
} from '../../../api';

interface ExpenseAndTermsFormProps {
  entity: ConnectedFacility | BookingRequestRead;
  entityType: 'connection' | 'booking';
  onUpdate: () => void;
  showAdditionalTerms?: boolean;
  sectionLabel?: string;
}

const ToggleButton = ({
  entityType,
  showForm,
  onClick,
  sectionLabel,
}: {
  entityType: 'connection' | 'booking';
  showForm: boolean;
  onClick: () => void;
  sectionLabel: string;
}) => {
  if (entityType === 'booking') {
    return (
      <button
        type="button"
        onClick={onClick}
        className="d-flex align-items-center text-section-name--dark-brand mb-3 border-0 bg-transparent h5 p-0"
      >
        {showForm ? <BsCaretDownFill className="me-2" /> : <BsCaretRightFill className="me-2" />}
        {sectionLabel}
      </button>
    );
  }

  return (
    <Button
      variant="link"
      className="p-0 text-decoration-none d-flex align-items-center gap-2"
      onClick={onClick}
    >
      {showForm ? <BsCaretDownFill /> : <BsCaretRightFill />}
      <span>{sectionLabel}</span>
    </Button>
  );
};

export const ExpenseAndTermsForm = ({
  entity,
  entityType,
  onUpdate,
  showAdditionalTerms = true,
  sectionLabel = 'Expenses & Terms',
}: ExpenseAndTermsFormProps) => {
  const [showForm, setShowForm] = useState(false);
  const [expectedExpenses, setExpectedExpenses] = useState<ExpectedExpensesEnum[]>(
    entity.expected_expenses || []
  );
  const [additionalTerms, setAdditionalTerms] = useState(entity.additional_terms || '');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const queryClient = useQueryClient();

  const updateMutation = useMutation({
    mutationFn: async () => {
      if (entityType === 'connection') {
        return ConnectedFacilityService.connectedFacilityPartialUpdate({
          id: entity.id,
          requestBody: {
            expected_expenses: expectedExpenses,
            additional_terms: additionalTerms,
          },
        });
      } else {
        // Booking request - use the full update endpoint
        return BookingRequestsUpdateService.bookingRequestsUpdatePartialUpdate({
          requestId: entity.id,
          requestBody: {
            expected_expenses: expectedExpenses,
            additional_terms: additionalTerms,
          } as ProviderBookingRequestWriteDetail,
        });
      }
    },
    onSuccess: () => {
      setSaveSuccess(true);
      setSaveError(null);
      if (entityType === 'connection') {
        queryClient.invalidateQueries({ queryKey: ['connected-facilities'] });
      } else {
        queryClient.invalidateQueries({ queryKey: ['booking-requests'] });
      }
      onUpdate();
      setTimeout(() => setSaveSuccess(false), 3000);
    },
    onError: (error: any) => {
      setSaveError(error.message || 'Failed to update');
      setSaveSuccess(false);
    },
  });

  const handleExpenseChange = (expense: ExpectedExpensesEnum) => {
    setExpectedExpenses((prev) =>
      prev.includes(expense) ? prev.filter((e) => e !== expense) : [...prev, expense]
    );
  };

  const handleSave = () => {
    updateMutation.mutate();
  };

  const { data: expenseOptions = [] } =
    useCodeDefinitionsAsOptions<ExpectedExpensesEnum>('ExpenseCategory');

  return (
    <div className="mt-3">
      <ToggleButton
        entityType={entityType}
        showForm={showForm}
        onClick={() => setShowForm(!showForm)}
        sectionLabel={sectionLabel}
      />

      {showForm && (
        <div className="mt-2 border p-3 rounded bg-light">
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>
                <div className="tw:text-sm tw:italic tw:text-zinc-600 dark:tw:text-zinc-300">
                  Select any anticipated expenses for assignments at this location
                </div>
              </Form.Label>
              <div>
                {expenseOptions.map((option) => (
                  <Form.Check
                    key={option.value}
                    type="checkbox"
                    id={`expense-${entityType}-${entity.id}-${option.value}`}
                    label={option.label}
                    checked={expectedExpenses.includes(option.value)}
                    onChange={() => handleExpenseChange(option.value)}
                  />
                ))}
              </div>
            </Form.Group>

            {showAdditionalTerms && (
              <Form.Group className="mb-3">
                {/* <Form.Label>Additional Terms</Form.Label> */}
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={additionalTerms}
                  onChange={(e) => setAdditionalTerms(e.target.value)}
                  placeholder="Enter any additional terms for the confirmation letter..."
                />
              </Form.Group>
            )}

            {saveSuccess && (
              <Alert variant="success" className="py-2">
                Successfully updated!
              </Alert>
            )}

            {saveError && (
              <Alert variant="danger" className="py-2">
                {saveError}
              </Alert>
            )}

            <div className="d-flex gap-2">
              <Button
                variant="primary"
                size="sm"
                onClick={handleSave}
                disabled={updateMutation.isPending}
              >
                {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={() => setShowForm(false)}>
                Cancel
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
};
